/**
 * Session-token-first embedded bootstrap (managed-install migration).
 *
 * Wraps POST /shopify/session: App Bridge session token in, Assisty JWT out.
 * Every function returns a boolean so App.vue can fall back to the legacy
 * GET /shopify hmac path whenever this path can't complete — a merchant is
 * never worse off than the pre-migration behavior.
 */
import { getIdToken, isEmbedded } from '@/utils/shopifyBridge';
import { post, setIdentityToken } from '@/services/http';

interface SessionResponse {
  token: string;
  store: { id: number; shop_name: string };
}

export interface SessionAuthDeps {
  enabled: () => boolean;
  embedded: () => boolean;
  getIdToken: () => Promise<string | null>;
  post: <T>(url: string, data?: any) => Promise<T>;
  setIdentityToken: (token: string, storeName: string) => void;
}

export function isSessionAuthEnabled(): boolean {
  return process.env.VUE_APP_SHOPIFY_SESSION_AUTH === 'true';
}

const defaultDeps: SessionAuthDeps = {
  enabled: isSessionAuthEnabled,
  embedded: isEmbedded,
  getIdToken,
  post,
  setIdentityToken,
};

async function postSession(idToken: string, deps: SessionAuthDeps): Promise<boolean> {
  const res = await deps.post<SessionResponse>('/shopify/session', { id_token: idToken });
  if (!res || !res.token || !res.store?.shop_name) {
    return false;
  }
  deps.setIdentityToken(res.token, res.store.shop_name);
  return true;
}

function isUnauthorized(e: any): boolean {
  return e?.response?.status === 401;
}

/** Referrer flow: authenticate with an id_token we already hold. */
export async function authenticateWithSessionToken(
  idToken: string,
  deps: SessionAuthDeps = defaultDeps,
): Promise<boolean> {
  try {
    return await postSession(idToken, deps);
  } catch {
    return false;
  }
}

/**
 * Embedded bootstrap: mint a session token and trade it for an Assisty JWT.
 * On 401 (session tokens live ~1 min, so "stale" is routine) retry exactly
 * once with a fresh token; any other failure means "use the legacy path".
 */
export async function attemptShopifySessionAuth(
  deps: SessionAuthDeps = defaultDeps,
): Promise<boolean> {
  if (!deps.enabled() || !deps.embedded()) {
    return false;
  }
  const idToken = await deps.getIdToken();
  if (!idToken) {
    return false;
  }
  try {
    return await postSession(idToken, deps);
  } catch (e: any) {
    if (!isUnauthorized(e)) {
      return false;
    }
    const fresh = await deps.getIdToken();
    if (!fresh) {
      return false;
    }
    try {
      return await postSession(fresh, deps);
    } catch {
      return false;
    }
  }
}

/** In-flight re-exchange, so a burst of 401s (or of Atlas REQUEST_AUTHs) makes one. */
let renewalInFlight: Promise<string | null> | null = null;

/**
 * AS-11299: re-run the boot exchange mid-session, when the Assisty JWT has
 * expired (or the server rejected it). Resolves the new JWT, already stored
 * via `setIdentityToken`, or null when session auth is off, the app is not
 * embedded, or the exchange is refused — the caller then falls back to its
 * pre-existing eviction behaviour.
 *
 * Embedded OWNER sessions have no refresh token, so this is their only way
 * to renew without a reload.
 */
export function renewShopifySessionOnce(
  deps: SessionAuthDeps = defaultDeps,
): Promise<string | null> {
  if (renewalInFlight) {
    return renewalInFlight;
  }
  let minted: string | null = null;
  // `.finally` clears the slot on failure too, so one refused exchange does
  // not answer every later 401 with the same null.
  renewalInFlight = attemptShopifySessionAuth({
    ...deps,
    setIdentityToken: (token: string, storeName: string) => {
      minted = token;
      deps.setIdentityToken(token, storeName);
    },
  })
    .then((ok) => (ok ? minted : null))
    .catch(() => null)
    .finally(() => {
      renewalInFlight = null;
    });
  return renewalInFlight;
}
